<template>
  <div class="ui-filters">
    <ElPopover
      v-model:visible="filtersOpen"
      placement="bottom"
      trigger="manual"
      width="380"
      popper-class="ui-filters_wrapper"
    >
      <template v-slot:reference>
        <div
          ref="mainBtn"
          v-click-outside="handleClickOutside"
          class="btn btn-refresh btn-sm !tw-flex"
          :class="{ 'btn-primary': filtersOpen || activeFilter,
                    'btn-outline-primary': !filtersOpen && !activeFilter }"
          @click="toggleFilters"
        >
          <i class="fas fa-filter mr-4" />
          {{ $t('ui.filters.inner.filters') }}
          <span
            v-if="!isShort && activeFilter"
            class="length-filter pl-2"
          >({{ filterLength }})</span>
        </div>
      </template>

      <div
        ref="pop"
        class="ui-filters_pop"
      >
        <div
          v-if="isMobile"
          class="ui-filters_title"
        >
          <div
            class="cross"
            @click="toggleFilters"
          >
            ×
          </div>{{ $t('filters.filter') }}
          <div
            v-if="activeFilter && isMobile"
            class="reset-filters text-red ml-8"
            @click="resetFilters"
          >
            {{ $t('filters.resetFilters') }}
          </div>
        </div>
        <div
          v-if="$store.state.profile.user.referral_program_enabled"
          class="filters-border-bottom mb-16"
        >
          <UiSelect
            v-if="useFilters.includes(allFiltersMeta.subAffiliate.type)"
            :key="subAffiliateFilter.type"
            v-model="subAffiliateFilter.value"
            :placeholder="subAffiliateFilter.placeholder"
            :label="subAffiliateFilter.title"
            :list="subAffiliateFilter.options || data[subAffiliateFilter.type]"
            :remote-search="subAffiliateFilter.remoteMethod"
            :disabled="subAffiliateFilter.dependsKey?.some(el => !allFilters[el].value)"
            is-search
            type="filter"
            :multiple="subAffiliateFilter.multiple"
            :teleported="false"
            mobile-shift-fix
            @change="subAffiliateFilter.change"
          />
        </div>
        <UiSelect
          v-for="(item, id) in currentFilter.filter(item => ![allFiltersMeta.subAffiliate.type].includes(item.type))"
          :key="id"
          v-model="item.value"
          :placeholder="$t('filters.all')"
          :label="item.title"
          :list="item.options || data[item.type]"
          :remote-search="item.remoteMethod"
          :disabled="item.dependsKey?.some(el => !allFilters[el].value)"
          is-search
          type="filter"
          :multiple="item.multiple"
          :teleported="false"
          mobile-shift-fix
          @change="item.change"
        />
        <div class="!tw-flex pl-16 pr-16 mb-16 pt-16 filter-buttons">
          <button
            :disabled="!activeFilter && !isFiltersChanged"
            class="btn btn-primary btn-refresh !tw-w-full"
            @click="applyFilters()"
          >
            <span class="before-icon">{{ $t('filters.applyFilters') }}</span>
          </button>
          <button
            v-if="activeFilter && !isMobile"
            class="btn btn-outline-danger btn-sm ml-8 !tw-w-full"
            @click="resetFilters"
          >
            <span class="text-red">×</span>
            {{ $t('filters.resetFilters') }}
          </button>
        </div>
      </div>
    </ElPopover>
    <template v-if="activeFilter && !isMobile">
      <div
        v-if="isShort"
        class="reset-filters-circle"
        @click="resetFilters"
      >
        ×
      </div>
      <div
        v-else
        class="reset-filters text-red ml-8"
        @click="resetFilters"
      >
        {{ $t('filters.resetFilters') }}
      </div>
    </template>
  </div>
</template>

<script>

import ResponsiveMixin from '@/components/Helpers/responsive-mixin';

export default {
  name: 'UiFilters',

  mixins: [
    ResponsiveMixin,
  ],

  props: {
    isShort: {
      type: Boolean,
      default: false,
    },
    useFilters: {
      type: Array,
      default() {
        return [
          'traffic_source_id',
          'country_code',
          'player_id',
          'affiliate_offer_id',
          'subid1',
          'subid2',
          'subid3',
          'subid4',
          'subid5',
        ];
      },
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    route: {
      type: String,
      default: () => (''),
    },
  },
  emits: ['input', 'reset'],

  data() {
    return {
      filtersOpen: false,
      allFilters: {},
      tmpFilters: {},
      data: {
        subAffiliate: [],
        offersByAffiliateId: [],
        trafficSourceByAffiliateId: [],
        mediaBuyingRootCampaignId: [],
        mediaBuyingCampaignId: [],
        marketingMaterialId: [],
      },
      initialFilters: {},
    };
  },

  watch: {
    '$i18n.locale': function () {
      this.initializeFilters(true);
    },
    allFiltersMeta: {
      immediate: true,
      deep: true,
      handler(value) {
        this.allFilters = {
          ...this.allFilters,
          ...value,
        };
      },
    },
    useFilters: {
      deep: true,
      immediate: true,
      handler() {
        this.initializeFilters();
      },
    },
  },

  computed: {
    subAffiliateFilter() {
      return this.currentFilter.find((el) => el.type === 'subAffiliate');
    },
    statusFilters() {
      return ['', 'draft', 'on_review', 'for_rework', 'approved', 'declined'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.posts.status.${type === '' ? 'all_status' : type}`),
      }));
    },
    offersFilters() {
      return [
        { id: 'all', text: this.$t('filters.type.all') },
        ...this.$store.state.offers.my.map((offer) => ({
          id: offer.id,
          text: offer.name,
        })),
      ];
    },
    cpaStatusFilters() {
      return [
        {
          id: 'pending_qualification',
          text: this.$t('mediaBuying.payments.status.pending_qualification'),
        },
        {
          id: 'in_hold',
          text: this.$t('mediaBuying.payments.status.in_hold'),
        },
        {
          id: 'on_review',
          text: this.$t('mediaBuying.payments.status.on_review'),
        },
        {
          id: 'approved',
          text: this.$t('mediaBuying.payments.status.approved'),
        },
        {
          id: 'declined',
          text: this.$t('mediaBuying.payments.status.declined'),
        },
      ];
    },

    typeFilters() {
      return ['', 'publication', 'cancellation'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.posts.type.${type === '' ? 'all_type' : type}`),
      }));
    },

    allFiltersMeta() {
      return {
        type: {
          type: 'type',
          field: 'type',
          value: 'all',
          title: this.$t('filters.type.title'),
          options: [
            {
              id: 'all',
              text: this.$t('filters.type.all'),
            },
            {
              id: 'landing',
              text: this.$t('filters.type.landing'),
            },
            {
              id: 'html5_banner',
              text: this.$t('filters.type.html5_banner'),
            },
            {
              id: 'simple_banner',
              text: this.$t('filters.type.simple_banner'),
            },
          ],
        },
        language: {
          type: 'language',
          field: 'language_code',
          value: 'all',
          title: this.$t('filters.language.title'),
          options: this.languages,
        },
        brand: {
          type: 'brand',
          field: 'site_id',
          value: 'all',
          title: this.$t('filters.brand.title'),
          options: this.brands,
          change: async () => {
            this.data.marketingMaterialId = await this.fetchMarketingMaterial();

            this.currentFilter.find((el) => el.type === 'marketingMaterialId').value = '';
          },
        },
        visibility: {
          type: 'visibility',
          field: 'visibility',
          title: this.$t('filters.visibility.title'),
          value: 'all',
          options: [
            {
              id: 'all',
              text: this.$t('filters.visibility.all'),
            },
            {
              id: 'public',
              text: this.$t('filters.visibility.public'),
            },
            {
              id: 'private',
              text: this.$t('filters.visibility.private'),
            },
          ],
        },

        typePosts: {
          type: 'typePosts',
          field: 'typePosts',
          value: '',
          title: this.$t('filters.type.title'),
          options: this.typeFilters,
        },

        statusPosts: {
          type: 'statusPosts',
          field: 'statusPosts',
          value: '',
          title: 'statusPosts',
          options: this.statusFilters,
        },
        offers: {
          type: 'offers',
          field: 'affiliate_offer_id',
          value: 'all',
          title: this.$t('filters.offer.title'),
          options: this.offersFilters,
        },
        cpaStatus: {
          type: 'cpaStatus',
          field: 'cpa_status',
          value: [],
          title: this.$t('filters.status.title'),
          options: this.cpaStatusFilters,
          placeholder: this.$t('filters.type.all'),
          multiple: true,
        },
        traffic: {
          type: 'traffic',
          field: 'traffic_type',
          value: '',
          title: this.$t('filters.traffic'),
          options:  [
            {
              id: '',
              text: this.$t('filters.all'),
            },
            {
              id: 'affiliates',
              text: this.$t('filters.partners'),
            },
            {
              id: 'direct',
              text: this.$t('filters.direct'),
            },
          ],
        },
        players: {
          type: 'players',
          field: 'players_filter',
          value: '',
          title: this.$t('filters.players'),
          placeholder: this.$t('filters.type.all'),
          options:  [
            {
              id: '',
              text: this.$t('filters.all'),
            },
            {
              id: 'new',
              text: this.$t('customReports.new'),
            },
            {
              id: 'old',
              text: this.$t('customReports.old'),
            },
          ],
        },
        subAffiliate: {
          type: 'subAffiliate',
          field: 'affiliate_id',
          search: true,
          value: '',
          title: this.$t('filters.subaffiliate'),
          placeholder: this.$t('filters.selectSA'),
          remoteMethod: this.fetchSubAffiliate,
          change: async (id, data) => {
            this.tmpFilters.subAffiliate = {
              id: data?.id,
              email: data?.text,
            };

            this.data.offersByAffiliateId = await this.fetchOffersByAffiliateId();
            this.data.trafficSourceByAffiliateId = await this.fetchTrafficSourceByAffiliateId();
            this.data.marketingMaterialId = await this.fetchMarketingMaterial();

            this.currentFilter.find((el) => el.type === 'offersByAffiliateId').value = '';
            this.currentFilter.find((el) => el.type === 'trafficSourceByAffiliateId').value = '';
            this.currentFilter.find((el) => el.type === 'marketingMaterialId').value = '';
          },
        },
        offersByAffiliateId: {
          type: 'offersByAffiliateId',
          field: 'affiliate_offer_id',
          search: true,
          value: '',
          title: this.$t('filters.offer.title'),
          placeholder: this.$t('filters.type.all'),
          remoteMethod: this.fetchOffersByAffiliateId,
          change: async (id, data) => {
            this.tmpFilters.offersByAffiliateId = {
              id: data?.id,
              name: data?.text,
            };
          },
        },
        trafficSourceByAffiliateId: {
          type: 'trafficSourceByAffiliateId',
          field: 'traffic_source_id',
          search: true,
          value: '',
          title: this.$t('filters.trafficSource'),
          placeholder: this.$t('filters.type.all'),
          remoteMethod: this.fetchTrafficSourceByAffiliateId,
          change: async (id, data) => {
            this.tmpFilters.trafficSourceByAffiliateId = {
              id: data?.id,
              name: data?.text,
            };
          },
        },
        marketingMaterialId: {
          type: 'marketingMaterialId',
          field: 'marketing_material_id',
          search: true,
          value: '',
          title: this.$t('filters.marketingMaterial'),
          placeholder: this.$t('filters.type.all'),
          remoteMethod: this.fetchMarketingMaterial,
          change: async (id, data) => {
            this.tmpFilters.marketingMaterialId = {
              id: data?.id,
              name: data?.text,
            };
          },
        },

        mediaBuyingRootCampaignId: {
          type: 'mediaBuyingRootCampaignId',
          field: 'media_buying_root_campaign_id',
          search: true,
          value: '',
          title: this.$t('filters.mediaBuyingRootCampaign'),
          placeholder: this.$t('filters.type.all'),
          remoteMethod: (search) => this.fetchCampaignsId(search, { isRoot: true }),
          change: async (data) => {
            this.tmpFilters.mediaBuyingRootCampaignId = {
              id: data?.id,
              name: data?.text,
            };

            this.data.mediaBuyingCampaignId = [];
            this.currentFilter.find((el) => el.type === 'mediaBuyingCampaignId').value = '';

            if (data) {
              this.data.mediaBuyingCampaignId = await this.fetchCampaignsId('', { root_campaign_id: data });
            }
          },
        },
        mediaBuyingCampaignId: {
          type: 'mediaBuyingCampaignId',
          field: 'media_buying_campaign_id',
          dependsKey: ['mediaBuyingRootCampaignId'],
          search: true,
          value: '',
          title: this.$t('filters.mediaBuyingCampaign'),
          placeholder: this.$t('filters.type.all'),
          remoteMethod: (search) => {
            if (this.allFiltersMeta.mediaBuyingRootCampaignId.value) {
              return this.fetchCampaignsId(search, { root_campaign_id: this.allFiltersMeta.mediaBuyingRootCampaignId.value });
            }
          },
          change: async (id, data) => {
            this.tmpFilters.mediaBuyingCampaignId = {
              id: data?.id,
              name: data?.text,
            };
          },
        },

        playerCountryCode: {
          type: 'playerCountryCode',
          field: 'player_country_code',
          search: true,
          value: '',
          title: this.$t('filters.playersRegistration'),
          placeholder: this.$t('filters.type.all'),
          options: [
            {
              id: '',
              text: this.$t('filters.type.all'),
            },
            ...this.$store.state.misc.countries.map((el) => ({ id: el.code, text: el.name })),
          ],
        },
      };
    },

    languages() {
      const languages = this.$store.state.misc.languages.map((l) => ({
        id: l.code,
        text: l.name,
      }));

      return [{
        id: 'all',
        text: this.$t('filters.language.all'),
      }, ...languages];
    },
    brands() {
      const brands = this.$store.state.media.sites.map((e) => ({
        id: e.id,
        text: e.name,
      }));
      return [{
        id: 'all',
        text: this.$t('filters.type.all'),
      }, ...brands];
    },
    currentFilter() {
      return this.initFilters();
    },
    filterLength() {
      return this.currentFilter.filter((e) => e.value !== '' && e.value !== 'all' && e.value.length !== 0).length;
    },

    activeFilter() {
      return this.filterLength > 0;
    },
    isFiltersChanged() {
      const current = this.currentFilter.reduce((acc, filter) => ({
        ...acc,
        [filter.field]: filter.value,
      }), {});

      return Object.entries(current)
      .some(([key, value]) => !this.$_.isEqual(value, this.initialFilters[key]));
    },
  },

  methods: {
    async initializeFilters(langSwitch = false) {
      await Promise.all(this.currentFilter.map(async (el) => {
        if (el.remoteMethod && (langSwitch || !this.data[el.type]?.length)) {
          try {
            this.data[el.type] = await el.remoteMethod('', { isInit: true });
          } catch (error) {
            this.data[el.type] = [];
          }
        }
      }));
    },
    initFilters() {
      const filteredArray = this.$_.filter(this.allFilters, (item) => this.useFilters.indexOf(item.type) !== -1);
      filteredArray.sort((a, b) => this.useFilters.indexOf(a.type) - this.useFilters.indexOf(b.type));

      filteredArray.forEach((item) => {
        if (this.filters !== null) {
          item.value = this.filters[item.field];
        } else {
          item.value = 'all';
        }
      });
      return filteredArray;
    },
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
      this.initFilters();

      if (this.filtersOpen) {
        this.initialFilters = this.currentFilter.reduce((acc, filter) => ({
          ...acc,
          [filter.field]: filter.value,
        }), {});
      }
    },

    handleClickOutside(event) {
      const triggerEl = this.$refs.pop;
      const popoverEl = document.querySelector('.el-popover');
      if (
        triggerEl &&
        !triggerEl.contains(event.target) &&
        !popoverEl?.contains(event.target)
      ) {
        this.filtersOpen = false;
        this.initFilters();
      }
    },

    initDictionaries() {
      this.allFilters.brand.options = this.brands;
      this.allFilters.language.options = this.languages;
    },

    applyFilters() {
      const val = {
        ...this.tmpFilters,
      };
      this.$_.forEach(this.currentFilter, (filter) => {
        this.$_.assign(val, { [filter.field]: filter.value });
      });
      this.$emit('input', val);
      this.filtersOpen = false;
      this.initialFilters = {};
    },
    resetFilters() {
      this.$emit('reset');
      this.initFilters();
      this.filtersOpen = false;
      this.initialFilters = {};
    },
    async fetchMarketingMaterial(search, { isInit } = {}) {
      const { payload } = await this.$api.getMarketingMaterials( {
        search,
        limit: 50,
        ...(this.allFiltersMeta.subAffiliate.value !== '' && { sub_affiliate_id: this.allFiltersMeta.subAffiliate.value }),
        ...(this.allFiltersMeta.brand.value !== 'all' && { site_id: this.allFiltersMeta.brand.value }),
      });

      let currentOptions = [];
      if (isInit && this.filters['marketingMaterialId']) {
        currentOptions = [this.filters['marketingMaterialId']];
      }

      return [
        { id: '', text: this.$t('filters.type.all') },
        ...this.$_.uniqBy([...currentOptions, ...payload], 'id').map((el) => ({ id: el.id, text: el.name })),
      ];
    },
    async fetchTrafficSourceByAffiliateId(search, { isInit } = {}) {
      const { payload } = await this.$api.getTrafficSources( {
        search,
        sub_affiliate_id: this.allFiltersMeta.subAffiliate.value,
      });

      let currentOptions = [];
      if (isInit && this.filters['trafficSourceByAffiliateId']) {
        currentOptions = [this.filters['trafficSourceByAffiliateId']];
      }

      return [
        { id: '', text: this.$t('filters.type.all') },
        ...this.$_.uniqBy([...currentOptions, ...payload], 'id').map((el) => ({ id: el.id, text: el.name })),
      ];
    },
    async fetchOffersByAffiliateId(search, { isInit } = {}) {
      const { payload } = await this.$api.getAffiliateOffers( {
        search,
        sub_affiliate_id: this.allFiltersMeta.subAffiliate.value,
      });

      let currentOptions = [];
      if (isInit && this.filters['offersByAffiliateId']) {
        currentOptions = [this.filters['offersByAffiliateId']];
      }

      return [
        { id: '', text: this.$t('filters.type.all') },
        ...this.$_.uniqBy([...currentOptions, ...payload], 'id').map((el) => ({ id: el.id, text: el.name })),
      ];
    },

    async fetchSubAffiliate(search, { isInit } = {}) {
      const { payload } = await this.$api.getSubAffiliates( { search, limit: 100 });

      let currentOptions = [];
      if (isInit && this.filters['subAffiliate']) {
        currentOptions = [this.filters['subAffiliate']];
      }

      return this.$_.uniqBy([
        {
          id: '',
          email: this.$t('mediaBuying.campaign.my_account'),
        },
        ...currentOptions,
        ...payload
      ], 'id').map((el) => ({ id: el.id, text: el.email }));
    },
    async fetchCampaignsId(search = '', { root_campaign_id = 'null', isRoot } = {}, { isInit } = {}) {
      const { payload } = await this.$api.getCampaigns({ root_campaign_id, search });

      let currentOptions = [];
      const key = isRoot ?'mediaBuyingRootCampaignId' : 'mediaBuyingCampaignId';

      if (isInit && this.filters[key]) {
        currentOptions = [this.filters[key]];
      }

      return [
        { id: '', text: this.$t('filters.type.all') },
        ...payload.map((el) => ({ id: el.id, text: el.name })),
      ];
    },
  },
};
</script>


<style lang="scss">
  // vue3-migration -- '@/assets/theme/common-page-styles.scss'
  // @import 'src/assets/theme/default/components/UiFilters';
  .ui-filters_pop {
    .ui-select__label {
      max-width: 50%;
    }
    .ui-select_filter .el-select__selection {
      max-width: calc(50% - 36px);
    }
    .el-select__selected-item.el-select__placeholder {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

</style>
